import { delay } from "../client.js";

const STORAGE_KEY = "console-design.sim-types.v1";

export const SIM_COLUMN_TYPES = ["string", "int", "file"];
export const SIM_TYPE_STATUSES = ["active", "deprecated"];

const SEED_SIM_TYPES = [
  {
    id: "st-1",
    name: "M2M",
    dataPlan: "1GB/month",
    roaming: false,
    status: "active",
    columns: [
      { key: "apn", type: "string" },
      { key: "sms_limit", type: "int" },
    ],
    createdAt: "2025-01-08T08:00:00.000Z",
    updatedAt: "2025-03-10T10:00:00.000Z",
  },
  {
    id: "st-2",
    name: "Standard",
    dataPlan: "5GB/month",
    roaming: false,
    status: "active",
    columns: [{ key: "apn", type: "string" }],
    createdAt: "2025-01-12T09:00:00.000Z",
    updatedAt: "2025-01-12T09:00:00.000Z",
  },
  {
    id: "st-3",
    name: "Roaming",
    dataPlan: "2GB/month",
    roaming: true,
    status: "active",
    columns: [
      { key: "apn", type: "string" },
      { key: "roaming_profile", type: "string" },
    ],
    createdAt: "2025-02-01T09:00:00.000Z",
    updatedAt: "2025-02-01T09:00:00.000Z",
  },
  {
    id: "st-4",
    name: "Legacy",
    dataPlan: "512MB/month",
    roaming: false,
    status: "deprecated",
    columns: [],
    createdAt: "2024-06-01T09:00:00.000Z",
    updatedAt: "2025-01-01T09:00:00.000Z",
  },
];

function cloneSeed() {
  return SEED_SIM_TYPES.map((row) => ({
    ...row,
    columns: row.columns.map((c) => ({ ...c })),
  }));
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneSeed();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : cloneSeed();
  } catch {
    return cloneSeed();
  }
}

function saveStore(rows) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

function nextId() {
  return `st-${Date.now().toString(36)}`;
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns)) return [];
  return columns
    .map((col) => ({ key: String(col.key ?? "").trim(), type: col.type }))
    .filter((col) => col.key && SIM_COLUMN_TYPES.includes(col.type));
}

function validatePayload(payload) {
  const name = payload.name?.trim();
  if (!name) throw new Error("SIM type name is required");
  const dataPlan = payload.dataPlan?.trim();
  if (!dataPlan) throw new Error("Data plan is required");
  const roaming = Boolean(payload.roaming);
  const status = payload.status?.trim();
  if (!SIM_TYPE_STATUSES.includes(status)) throw new Error("Select a valid status");
  const columns = normalizeColumns(payload.columns);
  const keys = new Set();
  for (const col of columns) {
    const lower = col.key.toLowerCase();
    if (keys.has(lower)) throw new Error(`Duplicate column key: ${col.key}`);
    keys.add(lower);
  }
  return { name, dataPlan, roaming, status, columns };
}

export async function listSimTypes() {
  await delay(110);
  return loadStore().map((row) => ({ ...row, columns: row.columns?.map((c) => ({ ...c })) ?? [] }));
}

export async function createSimType(payload) {
  await delay(140);
  const data = validatePayload(payload);
  const rows = loadStore();
  if (rows.some((r) => r.name.toLowerCase() === data.name.toLowerCase())) {
    throw new Error("A SIM type with this name already exists");
  }
  const now = new Date().toISOString();
  const row = { id: nextId(), ...data, createdAt: now, updatedAt: now };
  rows.push(row);
  saveStore(rows);
  return { ...row, columns: data.columns.map((c) => ({ ...c })) };
}

export async function updateSimType(simTypeId, payload) {
  await delay(120);
  const data = validatePayload(payload);
  const rows = loadStore();
  const row = rows.find((r) => r.id === simTypeId);
  if (!row) throw new Error("SIM type not found");
  if (rows.some((r) => r.id !== simTypeId && r.name.toLowerCase() === data.name.toLowerCase())) {
    throw new Error("A SIM type with this name already exists");
  }
  Object.assign(row, data, { updatedAt: new Date().toISOString() });
  row.columns = data.columns.map((c) => ({ ...c }));
  saveStore(rows);
  return { ...row, columns: row.columns.map((c) => ({ ...c })) };
}

export async function deleteSimType(simTypeId) {
  await delay(100);
  const rows = loadStore();
  const index = rows.findIndex((r) => r.id === simTypeId);
  if (index < 0) throw new Error("SIM type not found");
  rows.splice(index, 1);
  saveStore(rows);
}
